import {createSlice, createAsyncThunk} from '@reduxjs/toolkit'
import {AuthCheck, DownloadFileFn} from '../../helpers'
import {
  SearchBankCashAccountClient,
  GetBankCashAccountClient,
  DeleteAccountClient,
  CreateCashAccountClient,
  CreateBankAccountClient,
  ArchivingAccountClient,
  SearchTransactionsAccountClient,
  UpdateBankAccountClient,
  UpdateCashAccountClient,
  CurrentAccountStatementClient,
} from '../../api'

const SERVICE_URL = window.ENVVARS.REACT_APP_SERVICE_URL

const searchBankCashAccountClient = new SearchBankCashAccountClient(SERVICE_URL)
const getBankCashAccountClient = new GetBankCashAccountClient(SERVICE_URL)
const deleteAccountClient = new DeleteAccountClient(SERVICE_URL)
const createCashAccountClient = new CreateCashAccountClient(SERVICE_URL)
const createBankAccountClient = new CreateBankAccountClient(SERVICE_URL)
const archivingAccountClient = new ArchivingAccountClient(SERVICE_URL)
const searchTransactionsAccountClient = new SearchTransactionsAccountClient(SERVICE_URL)
const updateBankAccountClient = new UpdateBankAccountClient(SERVICE_URL)
const updateCashAccountClient = new UpdateCashAccountClient(SERVICE_URL)
const currentAccountStatementClient = new CurrentAccountStatementClient(SERVICE_URL)

const initialState = {
  loading: false,
  isRedirect: false,
  error: null,
  isDeleted: false,
  isArchived: false,
  excel: null,
  statement: null,
  account: null,
  accountList: null,
  detail: null,
}

// Async Thunks
export const accountGetAll = createAsyncThunk('account/getAll', async (params, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    dispatch(setAccountList(null))
    const response = await searchBankCashAccountClient.search(
      params.name,
      params.accountTypeId,
      params.projectId,
      params.currencyId,
      params.minBalance,
      params.maxBalance,
      params.items_per_page,
      params.page,
      params.order,
      params.sort
    )
    dispatch(setAccountListPage(response))
    return response
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const accountSearchTransactions = createAsyncThunk(
  'account/searchTransactions',
  async (accountTypeId, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      dispatch(setAccountDeleted(false))
      dispatch(setAccountArchived(false))
      dispatch(setAccountList(null))
      const response = await searchTransactionsAccountClient.search(accountTypeId)
      dispatch(setAccountList(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const accountGetBankCash = createAsyncThunk(
  'account/getBankCash',
  async (id, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await getBankCashAccountClient.get(id)
      dispatch(setAccountDetail(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const accountDelete = createAsyncThunk('account/delete', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    await deleteAccountClient.delete(id)
    dispatch(setAccountDeleted(true))
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const accountArchiving = createAsyncThunk('account/archive', async (id, {dispatch}) => {
  try {
    dispatch(setLoading(true))
    dispatch(setRedirect(false))
    await archivingAccountClient.archive(id)
    dispatch(setAccountArchived(true))
  } catch (error) {
    AuthCheck(error)
    dispatch(setError(error))
    throw error
  }
})

export const accountCreateCash = createAsyncThunk(
  'account/createCash',
  async (account, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createCashAccountClient.create(account)
      dispatch(setRedirect(response || account || true))
      dispatch(setAccountCreated(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const accountCreateBank = createAsyncThunk(
  'account/createBank',
  async (account, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await createBankAccountClient.create(account)
      dispatch(setRedirect(response || account || true))
      dispatch(setAccountCreated(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const accountUpdateCash = createAsyncThunk(
  'account/updateCash',
  async (account, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateCashAccountClient.update(account)
      dispatch(setAccountUpdated({isRedirect: response, detail: account}))
      dispatch(setRedirect(response || account || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const accountUpdateBank = createAsyncThunk(
  'account/updateBank',
  async (account, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await updateBankAccountClient.update(account)
      dispatch(setAccountUpdated({isRedirect: response, detail: account}))
      dispatch(setRedirect(response || account || true))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const accountStatement = createAsyncThunk(
  'account/statement',
  async (params, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await currentAccountStatementClient.get(params)
      dispatch(setAccountStatement(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

export const accountExcel = createAsyncThunk(
  'account/excel',
  async ({params, fileName}, {dispatch}) => {
    try {
      dispatch(setLoading(true))
      dispatch(setRedirect(false))
      const response = await searchBankCashAccountClient.excel(
        params.name,
        params.accountTypeId,
        params.projectId,
        params.currencyId,
        params.minBalance,
        params.maxBalance,
        params.items_per_page,
        params.page,
        params.order,
        params.sort
      )
      if (response && response.data) {
        DownloadFileFn(response.data, fileName)
      }
      dispatch(setExcel(response))
      return response
    } catch (error) {
      AuthCheck(error)
      dispatch(setError(error))
      throw error
    }
  }
)

// Slice
const accountSlice = createSlice({
  name: 'account',
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload
    },
    setRedirect: (state, action) => {
      state.isRedirect = action.payload
      state.loading = false
    },
    setError: (state, action) => {
      state.error = action.payload
      state.loading = false
    },
    setAccountList: (state, action) => {
      state.accountList = action.payload
      state.loading = false
    },
    setAccountListPage: (state, action) => {
      state.accountListPage = action.payload
      state.loading = false
    },
    setAccountDetail: (state, action) => {
      state.detail = action.payload
      state.loading = false
    },
    setAccountArchived: (state, action) => {
      state.isArchived = action.payload
      state.loading = false
    },
    setAccountDeleted: (state, action) => {
      state.isDeleted = action.payload
      state.loading = false
    },
    setAccountCreated: (state, action) => {
      state.account = action.payload
      state.loading = false
    },
    setAccountUpdated: (state, action) => {
      state.detail = action.payload.detail
      state.loading = false
    },
    setAccountStatement: (state, action) => {
      state.statement = action.payload
      state.loading = false
    },
    setExcel: (state, action) => {
      state.excel = action.payload
      state.loading = false
    },
    resetState: (state) => {
      Object.assign(state, initialState)
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('account/') && action.type.endsWith('/pending'),
      (state) => {
        state.loading = true
      }
    )
  }
})

export const {
  setLoading,
  setRedirect,
  setError,
  setAccountList,
  setAccountListPage,
  setAccountDetail,
  setAccountArchived,
  setAccountDeleted,
  setAccountCreated,
  setAccountUpdated,
  setAccountStatement,
  setExcel,
  resetState,
} = accountSlice.actions

export default accountSlice.reducer
